
import { createSelector } from 'reselect';
import { values, keys } from 'lodash';
import Moment from 'moment';
import { fetchAWSCostReportActionTypes } from './../actions/types';
import { cachedRequestByIdReducer } from './helpers';
import { utils, aws } from './../helpers';
import {
  getAWSRegion,
  getAWSAccount,
  getAWSTableSortingKey,
  getAWSTableCurrentSortingOrders
} from './awsPageInfo';

const awsInstances = cachedRequestByIdReducer(fetchAWSCostReportActionTypes);
export default awsInstances;

const getAwsInstanceState = state => state.awsInstances;
export const awsInstancesById = state => getAwsInstanceState(state).byId;
export const getAwsInstanceById = (state, id) => awsInstancesById(state)[id];

export const getAwsInstances = createSelector(
  getAwsInstanceState,
  (instance) => values(instance.byId)
);

export const getAwsInstancesRequestStatus = createSelector(
  getAwsInstanceState,
  (instance) => instance.request.inProgress
);

export const getRunningInstances = createSelector(
  getAwsInstances,
  (instances) => {
    const result = instances !== undefined ?
      instances.filter(i => i.state.code === aws.STATES.RUNNING) : [];
    return result;
  }
);

export const groupAwsInstancesByRegion = createSelector(
  getAwsInstances,
  (instances) => utils.propertyAccumulatorReducer(instances, 'region')
);

export const groupAwsInstancesByAccount = createSelector(
  getAwsInstances,
  (instances) => utils.propertyAccumulatorReducer(instances, 'account')
);

export const getAWSAllRegions = createSelector(
  groupAwsInstancesByRegion,
  (instances) => { return keys(instances); }
);

export const getAWSAllAccounts = createSelector(
  groupAwsInstancesByAccount,
  (instances) => { return keys(instances); }
);

const costColumns = ['yesterday', 'today', 'tomorrow', 'annualized'];

const compareBy = (key, sortingOrder) => {
  return function (a, b) {
    if (!costColumns.includes(key))
    {
      if (a[key] < b[key]) return sortingOrder === 'desc' ? 1 : -1;
      if (a[key] > b[key]) return sortingOrder === 'desc' ? -1 : 1;
    }
    else
    {
      if (a[key] !== 'N/A' && b[key] !== 'N/A')
      {
        const costA = a[key].substring(1);
        const costB = b[key].substring(1);
        if (Number(costA) < Number(costB)) return sortingOrder === 'desc' ? 1 : -1;
        if (Number(costA) > Number(costB)) return sortingOrder === 'desc' ? -1 : 1;
      }
      else if (a[key] === 'N/A' && b[key] !== 'N/A')
      {
        return sortingOrder === 'desc' ? 1 : -1;
      }
      else if (a[key] !== 'N/A' && b[key] === 'N/A')
      {
        return sortingOrder === 'desc' ? -1 : 1;
      }
    }
    return 0;
  };
};

export const analyzeRunningInstancesCost = createSelector(
  getRunningInstances,
  getAWSRegion,
  getAWSAccount,
  (instances, region, account) => {
    const today = Moment().format('YYYY-MM-DD');
    const yesterday = Moment().startOf('day').subtract(1, 'days').format('YYYY-MM-DD');
    const tomorrow = Moment().startOf('day').add(1, 'days').format('YYYY-MM-DD');
    instances.forEach( i => {
      i['instanceType'] = i['instance_type'];
      i['today'] = 'N/A';
      i['yesterday'] = 'N/A';
      i['tomorrow'] = 'N/A';
      i['annualized'] = 'N/A';
      i['owner'] = '';
      i['bestBefore'] = '';
      i['otherTags'] = '';
      i.cost_and_usage.forEach(e => {
        const cost = Number(e.amortized_cost).toFixed(2);
        i['today'] = e.start === today ? '$' + cost : i['today'];
        i['yesterday'] = e.start === yesterday ? '$' + cost : i['yesterday'];
        i['tomorrow'] = e.start === tomorrow ? '$' + cost : i['tomorrow'];
        i['annualized'] = e.start === yesterday ?
          '$' + (cost * 365).toFixed(2) : i['annualized'];
      });
      i.tags.forEach(t => {
        i['owner'] = t.key.toLowerCase() === aws.MANDATORYTAGS.OWNER ?
          t.value : i['owner'];
        i['bestBefore'] = t.key.toLowerCase() === aws.MANDATORYTAGS.BESTBEFORE ?
          t.value : i['bestBefore'];
      });
      const otherTags = i.tags.filter(
        t => !values(aws.MANDATORYTAGS).includes(t.key.toLowerCase()));
      otherTags.forEach( t => {
        const line = t.key + ': ' + t.value + '\n';
        i['otherTags'] = i['otherTags'] + line;
      });
    }
    );
    instances = instances.filter(
      i => region === aws.DEFAULTREGION || i.region === region);
    instances = instances.filter(
      i => account === aws.DEFAULTACCOUNT || i.account === account);
    return instances;
  }
);

export const getSortedAnalyzedRunningInstancesCost = createSelector(
  analyzeRunningInstancesCost,
  getAWSTableSortingKey,
  getAWSTableCurrentSortingOrders,
  (instances, sortingKey, sortingOrders) => {
    instances.sort(compareBy(sortingKey, sortingOrders[sortingKey]));
    return instances;
  });

export const getGrandTotals = createSelector(
  analyzeRunningInstancesCost,
  (instances) => {
    const grandTotals = {
      grandTotalYesterday: 0,
      grandTotalAnnualized: 0
    };

    instances.forEach(i => {
      grandTotals['grandTotalYesterday'] = i['yesterday'] !== 'N/A' ?
        grandTotals['grandTotalYesterday'] + Number(i['yesterday'].substring(1)) :
        grandTotals['grandTotalYesterday'];
      grandTotals['grandTotalAnnualized'] = i['annualized'] !== 'N/A' ?
        grandTotals['grandTotalAnnualized'] + Number(i['annualized'].substring(1)) :
        grandTotals['grandTotalAnnualized'];
    });

    grandTotals['grandTotalYesterday'] = '$' +
     grandTotals['grandTotalYesterday'].toFixed(2);
    grandTotals['grandTotalAnnualized'] = '$' +
     grandTotals['grandTotalAnnualized'].toFixed(2);
    return grandTotals;
  });


// WEBPACK FOOTER //
// src/reducers/awsInstances.js